import { useApolloClient } from "@apollo/client";
import { KeyboardArrowDown as KeyboardArrowDownIcon } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Popover,
  Stack,
  Tooltip,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { TemplateSearchFilterPropertyFilterInput } from "../../../../clients/graphqlTypes.js";
// Reuses this existing query rather than defining a new one for the same
// shape (templateType props with labels) — avoids a second persisted-query
// operation for data already fetched here.
import { useTemplatesQuoteFilterChips_TemplateTypesQuery } from "../useTemplateDocumentsActiveFilterChips.generated.js";

/**
 * Design D9, reworked to match the "List pages with filters" convention
 * (`ButtonSelect`/`ButtonSelectMultiple`-style inline dropdown, not a
 * modal): the placeholder's originally-seeded quote filters, always shown
 * as the full fixed list (this control's own visibility never depends on
 * live filter state), each individually toggleable off/on the same way
 * every other quick filter works. Sits in the quick-filter row alongside
 * "Template type" / "Category" / "Project type"; the funnel FilterButton
 * stays untouched next to it — this is a quick provenance view, the funnel
 * remains the full editor.
 *
 * Loud (contained, primary) while the zero-result "filters hide matches"
 * state is active; muted (text) otherwise.
 *
 * Built on a plain MUI `Popover` rather than `@msys/ui`'s `PopoverOpenButton`
 * — that component forwards unrecognised props (which is where `Tooltip`
 * would land its hover handlers) onto the popover content, not the trigger
 * button, so wrapping it in a `Tooltip` the same way `ModalOpenButton` is
 * wrapped elsewhere in this file silently drops the hover. Managing the
 * anchor locally keeps `Tooltip` wrapping the real button directly.
 */
export function TemplatesQuoteFiltersChip({
  seededPropertyFilters,
  templateTypeIds,
  activeProperties,
  setActiveProperties,
  hiddenCount,
  isLoud,
}: {
  seededPropertyFilters: TemplateSearchFilterPropertyFilterInput[];
  templateTypeIds: string[];
  /** The live property filters — may also hold filters outside the seeded
   *  set (added via the full filter modal), which this control leaves
   *  untouched. */
  activeProperties: TemplateSearchFilterPropertyFilterInput[];
  setActiveProperties: (
    properties: TemplateSearchFilterPropertyFilterInput[]
  ) => void;
  /** Templates hidden by these filters — only known once C2a-A is showing. */
  hiddenCount?: number;
  isLoud: boolean;
}) {
  const { t } = useTranslate(["TemplatesSearch"]);
  const client = useApolloClient();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const templateTypesQuery = useTemplatesQuoteFilterChips_TemplateTypesQuery({
    client,
    variables: { ids: templateTypeIds },
    skip: templateTypeIds.length === 0,
  });
  const propertyLabels = new Map<string, string>();
  if (
    templateTypesQuery.data?.templateTypes.__typename === "TemplateTypesResult"
  ) {
    for (const templateType of templateTypesQuery.data.templateTypes
      .templateTypes) {
      for (const prop of templateType.props2) {
        propertyLabels.set(prop.key, prop.label);
      }
    }
  }

  if (seededPropertyFilters.length === 0) return null;

  const activeKeys = new Set(activeProperties.map(p => p.key));
  const seededKeys = new Set(seededPropertyFilters.map(p => p.key));

  const toggle = (key: string, checked: boolean) => {
    const newActiveKeys = checked
      ? new Set([...activeKeys, key])
      : new Set([...activeKeys].filter(k => k !== key));
    // Filters outside the seeded set (added independently via the full
    // filter modal) are left untouched — only the seeded ones are
    // reconstructed from the checkbox state.
    const keptOutsideSeededSet = activeProperties.filter(
      p => !seededKeys.has(p.key)
    );
    const newSeededActive = seededPropertyFilters.filter(p =>
      newActiveKeys.has(p.key)
    );
    setActiveProperties([...keptOutsideSeededSet, ...newSeededActive]);
  };

  return (
    <>
      <Tooltip
        title={
          hiddenCount !== undefined
            ? t(
                "These filters came from your quote — they're hiding {hiddenCount} templates.",
                { ns: "TemplatesSearch", hiddenCount }
              )
            : t("These come from the placeholder's search filters.", {
                ns: "TemplatesSearch",
              })
        }
      >
        <Button
          size="extra-small"
          variant={isLoud ? "contained" : "text"}
          color="primary"
          endIcon={<KeyboardArrowDownIcon fontSize="small" />}
          sx={{ textTransform: "none" }}
          onClick={e => setAnchorEl(e.currentTarget)}
        >
          {t("{quoteFilterCount} filters from quote", {
            ns: "TemplatesSearch",
            quoteFilterCount: seededPropertyFilters.length,
          })}
        </Button>
      </Tooltip>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Stack sx={{ p: 1, minWidth: 220 }}>
          {seededPropertyFilters.map(propertyFilter => (
            <FormControlLabel
              key={propertyFilter.key}
              control={
                <Checkbox
                  size="small"
                  checked={activeKeys.has(propertyFilter.key)}
                  onChange={(_e, checked) =>
                    toggle(propertyFilter.key, checked)
                  }
                />
              }
              label={
                propertyLabels.get(propertyFilter.key) ?? propertyFilter.key
              }
            />
          ))}
        </Stack>
      </Popover>
    </>
  );
}
